import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ClientForm({ open, onOpenChange, onSaved, initial }) {
  const empty = {
    first_name: "", last_name: "", phone: "", email: "", preferred_contact: "Email",
    type: "Residential", tier: "Standard",
    street: "", city: "Austin", state: "TX", zip: "",
    gate_code: "", parking: "", property_notes: "",
    internal_notes: "", customer_notes: "", crew_notes: "",
    requested_services: "", preferred_date: "", preferred_time: "", priority: "Normal",
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    setForm(initial ? { ...empty, ...initial } : empty);
    // eslint-disable-next-line
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (nextAction) => async (e) => {
    e.preventDefault();
    await onSaved(form, nextAction);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{initial ? "Edit Client" : "New Client"}</DialogTitle>
        </DialogHeader>
        <form className="space-y-6 mt-4">
          <Section title="Client Information">
            <Field label="First Name"><Input value={form.first_name} onChange={(e)=>set("first_name", e.target.value)} required data-testid="client-first-name" /></Field>
            <Field label="Last Name"><Input value={form.last_name} onChange={(e)=>set("last_name", e.target.value)} required data-testid="client-last-name" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e)=>set("phone", e.target.value)} placeholder="(512) 555-1234" data-testid="client-phone" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e)=>set("email", e.target.value)} data-testid="client-email" /></Field>
            <Field label="Preferred Contact">
              <Select value={form.preferred_contact} onValueChange={(v)=>set("preferred_contact", v)}>
                <SelectTrigger data-testid="client-pref-contact"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Phone">Phone</SelectItem>
                  <SelectItem value="Text">Text</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v)=>set("type", v)}>
                <SelectTrigger data-testid="client-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Property Information">
            <Field label="Street" full><Input value={form.street} onChange={(e)=>set("street", e.target.value)} data-testid="client-street" /></Field>
            <Field label="City"><Input value={form.city} onChange={(e)=>set("city", e.target.value)} data-testid="client-city" /></Field>
            <Field label="State"><Input value={form.state} onChange={(e)=>set("state", e.target.value)} data-testid="client-state" /></Field>
            <Field label="ZIP"><Input value={form.zip} onChange={(e)=>set("zip", e.target.value)} data-testid="client-zip" /></Field>
            <Field label="Gate Code"><Input value={form.gate_code} onChange={(e)=>set("gate_code", e.target.value)} data-testid="client-gate" /></Field>
            <Field label="Parking Instructions" full><Input value={form.parking} onChange={(e)=>set("parking", e.target.value)} data-testid="client-parking" /></Field>
            <Field label="Property Notes" full><Textarea rows={2} value={form.property_notes} onChange={(e)=>set("property_notes", e.target.value)} data-testid="client-property-notes" /></Field>
          </Section>

          <Section title="Requested Services">
            <Field label="Services" full><Input value={form.requested_services} onChange={(e)=>set("requested_services", e.target.value)} placeholder="e.g. Weekly Mowing, Mulch" data-testid="client-services" /></Field>
            <Field label="Preferred Date"><Input type="date" value={form.preferred_date} onChange={(e)=>set("preferred_date", e.target.value)} data-testid="client-pref-date" /></Field>
            <Field label="Preferred Time"><Input type="time" value={form.preferred_time} onChange={(e)=>set("preferred_time", e.target.value)} data-testid="client-pref-time" /></Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v)=>set("priority", v)}>
                <SelectTrigger data-testid="client-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Notes">
            <Field label="Internal Notes" full><Textarea rows={2} value={form.internal_notes} onChange={(e)=>set("internal_notes", e.target.value)} data-testid="client-internal-notes" /></Field>
            <Field label="Customer Notes" full><Textarea rows={2} value={form.customer_notes} onChange={(e)=>set("customer_notes", e.target.value)} data-testid="client-customer-notes" /></Field>
            <Field label="Crew Notes" full><Textarea rows={2} value={form.crew_notes} onChange={(e)=>set("crew_notes", e.target.value)} data-testid="client-crew-notes" /></Field>
          </Section>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="client-cancel">Cancel</Button>
            <Button type="button" variant="secondary" onClick={handleSubmit("estimate")} data-testid="client-save-estimate">Save + Create Estimate</Button>
            <Button type="button" variant="secondary" onClick={handleSubmit("job")} data-testid="client-save-job">Save + Schedule Job</Button>
            <Button type="button" onClick={handleSubmit("save")} data-testid="client-save">{initial ? "Update Client" : "Save Client"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="font-display text-base font-semibold border-b border-border pb-2">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
